import type { Session, SupabaseClient } from '@supabase/supabase-js';

/** The SDK owns persisted credentials and refresh. A late startup read must not
 * overwrite a newer sign-in, refresh or sign-out event. */
export function observeAuthSession(
    auth: SupabaseClient['auth'],
    onSession: (session: Session | null) => void,
    onError: () => void,
): () => void {
    let active = true;
    let eventSeen = false;
    const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
        if (!active) return;
        // The SDK also emits INITIAL_SESSION(null) when startup fails. Let the
        // startup read distinguish that error from a confirmed signed-out state.
        // A delayed initial notification must not replace a newer auth event.
        if (event === 'INITIAL_SESSION' && (eventSeen || !session)) return;
        if (session || event === 'SIGNED_OUT') {
            eventSeen = true;
            onSession(session);
        }
    });
    void auth.getSession().then(({ data, error }) => {
        if (!active || eventSeen) return;
        if (error) onError();
        else onSession(data.session);
    }).catch(() => { if (active && !eventSeen) onError(); });
    return () => { active = false; subscription.unsubscribe(); };
}
